import { useState } from 'react';
import { logError } from '../components/LogError';
import client from '../graphql/client';
import { appIsWorkingVar, isLoggedInVar } from '../graphql/cache';
import { ImpersonationInfo, clearImpersonationInfo, getImpersonationInfo, signOut } from '../services/auth';
import { NavigationPath } from '../types/DomainTypes';
import { useImpersonation } from './useImpersonation';

/**
 * Exposes the current impersonation session (if any) and a single `endSession`
 * action shared by the Impersonation screen and the global banner.
 */
export const useImpersonationSession = () => {
  const { impersonateLogout } = useImpersonation();
  const [activeSession] = useState<ImpersonationInfo | null>(() => getImpersonationInfo());

  const endSession = async () => {
    if (!activeSession) return;
    appIsWorkingVar(true);
    try {
      await impersonateLogout({
        impersonatorId: activeSession.impersonatorId,
        targetUserId: activeSession.targetUserId,
      });
    } catch (error) {
      logError(error as Error);
    } finally {
      clearImpersonationInfo();
      signOut();
      isLoggedInVar(false);
      // Wipe the Apollo in-memory cache so none of the impersonated client's
      // data survives into the next session, then hard-reload to clear all
      // remaining in-memory React/context state before returning to login.
      try {
        await client.clearStore();
      } catch (error) {
        logError(error as Error);
      }
      window.location.replace(NavigationPath.LOGIN);
    }
  };

  return { activeSession, endSession };
};
